import { useMemo, useState } from 'react';
import { Button, Modal, Select } from '@/components/ui';
import { useMeSpaces } from '@/hooks/useMeSpaces';
import { useVerifyAwsRole } from '@/hooks/useIdentityCredentials';
import { AwsRoleTestResult, NONE, useAgentsOfSpace } from './awsRoleShared';
import type { Credential, VerifyResult } from '@/types/identity';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  credential: Credential;
}

/**
 * "Test as agent…" for an `aws-role` credential: a simulated AssumeRole with
 * exactly the session tags a real run would send. Org and space come from the
 * credential itself (org roles may pick the space to simulate from); the admin
 * picks the agent, so trust policies that pin `optr-agent-id` can be tested
 * before any real run. Identity fills `optr-run-id` with a synthetic
 * `verify-…` id.
 */
export default function TestAwsRoleModal({ isOpen, onClose, credential }: Props) {
  const verify = useVerifyAwsRole();
  const { data: spacesData } = useMeSpaces();
  const isSpaceRole = credential.principal.kind === 'space-service';
  const sharedSpaces = useMemo(
    () => (spacesData?.items ?? []).filter((s) => s.kind !== 'personal'),
    [spacesData],
  );

  const [orgSpaceId, setOrgSpaceId] = useState<string>(NONE);
  const [agentId, setAgentId] = useState<string>(NONE);
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const spaceId = isSpaceRole ? credential.principal.id : orgSpaceId !== NONE ? orgSpaceId : undefined;
  const spaceName = sharedSpaces.find((s) => s.spaceId === spaceId)?.name ?? spaceId;
  const { data: agentsData } = useAgentsOfSpace(spaceId);
  const agents = agentsData?.items ?? [];

  const run = async () => {
    setError(null);
    setResult(null);
    try {
      const res = await verify.mutateAsync({
        credId: credential.credId,
        simulation: {
          ...(agentId !== NONE && { agentId }),
          ...(!isSpaceRole && spaceId && { spaceId }),
        },
      });
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Test failed');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Test IAM role as agent"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={run} isLoading={verify.isPending}>
            Run test
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="text-sm space-y-1">
          <p className="text-muted-foreground break-all">
            Role: <span className="font-mono text-foreground">{String(credential.metadata.roleArn ?? credential.providerInstanceId)}</span>
          </p>
          <p className="text-muted-foreground">
            Organization: <span className="font-mono text-foreground">{credential.orgId}</span>
          </p>
          {isSpaceRole && (
            <p className="text-muted-foreground">
              Space: <span className="text-foreground">{spaceName}</span>
            </p>
          )}
        </div>

        {!isSpaceRole && (
          <Select
            label="Simulate from space"
            value={orgSpaceId}
            onChange={(e: { target: { value: string } }) => {
              setOrgSpaceId(e.target.value);
              setAgentId(NONE);
            }}
            options={[
              { value: NONE, label: 'No space (optr-space-id omitted)' },
              ...sharedSpaces.map((s) => ({ value: s.spaceId, label: s.name })),
            ]}
          />
        )}

        <Select
          label="Simulate as agent"
          value={agentId}
          onChange={(e: { target: { value: string } }) => setAgentId(e.target.value)}
          disabled={!spaceId}
          hint={spaceId ? undefined : 'Pick a space to choose one of its agents.'}
          options={[
            { value: NONE, label: 'No agent (optr-agent-id omitted)' },
            ...agents.map((a) => ({ value: a.agentId, label: a.name })),
          ]}
        />

        {result && (
          <AwsRoleTestResult
            ok={result.ok}
            callerArn={result.callerArn}
            message={result.message}
            reason={result.ok ? undefined : result.reason}
            simulatedTags={result.simulatedTags}
          />
        )}
        {error && (
          <p className="text-sm text-status-error-fg" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
